import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Link } from "react-router-dom";
import {
  getExpansionGridColumnDescriptorSections,
  getProjectGridColumnDescriptors,
} from "../projectGridColumns";
import "./userMatrixPanel.css";
import { fetchAppConfig, postAdminCreateUser } from "./appConfigApi";
import {
  ADMINISTRATOR_TYPE_ID,
  CONTRACTOR_TYPE_ID,
  DEPARTMENT_OPTIONS,
  createInviteRecord,
  hydrateUserMatrixFromServer,
  loadUserMatrixSettings,
  markInviteEmailsSent,
  saveUserMatrixSettings,
} from "./userMatrixStorage";
import { postUserMatrixInviteEmail } from "./userMatrixInviteEmail";
import { rolePermissionOptionsByGroup, DEFAULT_ROLE_PERMISSIONS } from "./userMatrixRoleLabels";
import { isValidEmail, parseBatchEmails } from "./userMatrixBatchEmails";

function useUserMatrixBump() {
  const [v, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const on = () => bump();
    window.addEventListener("pb-user-matrix-settings-changed", on);
    return () => window.removeEventListener("pb-user-matrix-settings-changed", on);
  }, []);
  return v;
}

function ColumnLimitSection({ title, radioGroup, ids, descriptors, sections, onChange, hint }) {
  const restrict = ids !== null && ids !== undefined;
  const renderDescriptor = (d) => {
    const on = (ids || []).includes(d.colId);
    return (
      <label key={d.colId} className="user-matrix-col-check">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => {
            const next = new Set(ids || []);
            if (e.target.checked) next.add(d.colId);
            else next.delete(d.colId);
            onChange([...next]);
          }}
        />
        <span>{d.headerName}</span>
        <code className="user-matrix-col-id">{d.colId}</code>
      </label>
    );
  };
  return (
    <div className="user-matrix-col-section">
      <h5 className="user-matrix-col-section-title">{title}</h5>
      <p className="email-triggers-muted user-matrix-col-hint">
        {hint ||
          "Lists every column on the main project table (including ACF-backed fields). Fields that only appear on the project detail page are not listed here."}
      </p>
      <div className="user-matrix-col-mode-row">
        <label className="user-matrix-radio">
          <input
            type="radio"
            name={radioGroup}
            checked={!restrict}
            onChange={() => onChange(null)}
          />
          <span>All columns</span>
        </label>
        <label className="user-matrix-radio">
          <input
            type="radio"
            name={radioGroup}
            checked={restrict}
            onChange={() => onChange([])}
          />
          <span>Limit visible columns</span>
        </label>
      </div>
      {restrict ? (
        <div className="user-matrix-col-scroll" role="group" aria-label={`${title} columns`}>
          {Array.isArray(sections) && sections.length > 0
            ? sections.map((section) => (
                <div key={section.id} className="user-matrix-col-subsection">
                  <h6 className="user-matrix-col-subsection-title">{section.label}</h6>
                  {section.hint ? (
                    <p className="email-triggers-muted user-matrix-col-subsection-hint">{section.hint}</p>
                  ) : null}
                  {section.descriptors.map(renderDescriptor)}
                </div>
              ))
            : descriptors.map(renderDescriptor)}
        </div>
      ) : null}
    </div>
  );
}

function PermissionSection({ roleType, onChange }) {
  const permissions = roleType.permissions || {};
  const groups = rolePermissionOptionsByGroup();
  return (
    <div className="user-matrix-col-section">
      <h5 className="user-matrix-col-section-title">Permissions</h5>
      <p className="email-triggers-muted">
        These flags control board access and landing-page edit rights. Missing flags on older roles are
        filled from the role name when settings load; adjust checkboxes to override.
      </p>
      <div className="user-matrix-col-scroll" role="group" aria-label={`${roleType.name} permissions`}>
        {groups.map(({ group, options }) => (
          <div key={group} className="user-matrix-col-subsection">
            <h6 className="user-matrix-col-subsection-title">{group}</h6>
            {options.map((opt) => {
              const checked = Boolean(permissions[opt.key]);
              return (
                <label key={opt.key} className="user-matrix-col-check">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      onChange({
                        ...permissions,
                        [opt.key]: e.target.checked,
                      })
                    }
                  />
                  <span>{opt.label}</span>
                  <code className="user-matrix-col-id">{opt.key}</code>
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function DepartmentTagsSection({ roleType, onChange }) {
  const tags = Array.isArray(roleType.departmentTags) ? roleType.departmentTags : [];
  const active = new Set(tags);
  return (
    <div className="user-matrix-col-section">
      <h5 className="user-matrix-col-section-title">Department tags</h5>
      <p className="email-triggers-muted">
        When a role uses <strong>All columns</strong>, department tags auto-show or hide grouped fields
        (permits, circuit, MRC, etc.). They do not apply when <strong>Limit visible columns</strong> is selected.
      </p>
      <div className="user-matrix-col-scroll" role="group" aria-label={`${roleType.name} department tags`}>
        {DEPARTMENT_OPTIONS.map((opt) => (
          <label key={opt.id} className="user-matrix-col-check">
            <input
              type="checkbox"
              checked={active.has(opt.id)}
              onChange={(e) => {
                const next = new Set(tags);
                if (e.target.checked) next.add(opt.id);
                else next.delete(opt.id);
                onChange([...next]);
              }}
            />
            <span>{opt.label}</span>
            <code className="user-matrix-col-id">{opt.id}</code>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function UserMatrixPluginPanel() {
  const matrixRev = useUserMatrixBump();
  const commercialDescriptors = useMemo(() => getProjectGridColumnDescriptors(), []);
  const expansionDescriptorSections = useMemo(() => getExpansionGridColumnDescriptorSections(), []);
  const expansionDescriptors = useMemo(
    () => expansionDescriptorSections.flatMap((s) => s.descriptors),
    [expansionDescriptorSections]
  );
  const [settings, setSettings] = useState(() => loadUserMatrixSettings());
  const [newTypeName, setNewTypeName] = useState("");
  const [inviteEmailsText, setInviteEmailsText] = useState("");
  const [inviteTypeId, setInviteTypeId] = useState("");
  const [addUserPassword, setAddUserPassword] = useState("");
  const [lastInviteUrls, setLastInviteUrls] = useState([]);
  const [inviteError, setInviteError] = useState("");
  const [inviteEmailNotice, setInviteEmailNotice] = useState("");
  const [batchResults, setBatchResults] = useState([]);
  const [addUserBusy, setAddUserBusy] = useState(false);
  const [sendInviteBusy, setSendInviteBusy] = useState(false);
  const [saveNotice, setSaveNotice] = useState("");
  const [expandedRoleIds, setExpandedRoleIds] = useState(() => new Set());

  const parsedInviteEmails = useMemo(() => parseBatchEmails(inviteEmailsText), [inviteEmailsText]);
  const validInviteEmails = useMemo(
    () => parsedInviteEmails.filter((em) => isValidEmail(em)),
    [parsedInviteEmails]
  );
  const invalidInviteEmails = useMemo(
    () => parsedInviteEmails.filter((em) => !isValidEmail(em)),
    [parsedInviteEmails]
  );
  const inviteEmailCount = validInviteEmails.length;

  const toggleRoleExpanded = useCallback((roleId) => {
    setExpandedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }, []);

  useEffect(() => {
    setSettings(loadUserMatrixSettings());
  }, [matrixRev]);

  useEffect(() => {
    let cancelled = false;
    fetchAppConfig()
      .then((cfg) => {
        if (cancelled || !cfg?.userMatrix) return;
        hydrateUserMatrixFromServer(cfg.userMatrix);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback(async (next) => {
    setSettings(next);
    try {
      await saveUserMatrixSettings(next);
      setSaveNotice("");
      return true;
    } catch (e) {
      setSettings(loadUserMatrixSettings());
      const msg = e?.response?.data?.error || e?.message || "Could not save User Matrix settings.";
      setSaveNotice(msg);
      return false;
    }
  }, []);

  const updateUserType = useCallback(
    (id, patch) => {
      const userTypes = settings.userTypes.map((t) => (t.id === id ? { ...t, ...patch } : t));
      void persist({ ...settings, userTypes }).catch(() => {});
    },
    [settings, persist]
  );

  const addUserType = useCallback(() => {
    setSaveNotice("");
    const name = String(newTypeName || "").trim();
    if (!name) return;
    const nameKey = name.toLowerCase().replace(/\s+/g, " ");
    const existing = settings.userTypes.find(
      (t) => String(t.name || "").trim().toLowerCase().replace(/\s+/g, " ") === nameKey
    );
    if (existing) {
      setSaveNotice(`Role "${existing.name}" already exists — opened it instead of creating a duplicate.`);
      setNewTypeName("");
      setExpandedRoleIds((prev) => new Set(prev).add(existing.id));
      return;
    }
    const id = `role_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const next = {
      ...settings,
      userTypes: [
        ...settings.userTypes,
        {
          id,
          name,
          permissions: {
            ...DEFAULT_ROLE_PERMISSIONS,
            canViewMailLog: false,
            canManagePlugins: false,
            canManageUserMatrix: false,
            canAccessCommercial: true,
            canAccessExpansion: true,
          },
          commercialColumnIds: null,
          expansionColumnIds: null,
          departmentTags: [],
        },
      ],
    };
    void (async () => {
      const ok = await persist(next);
      if (ok) {
        setNewTypeName("");
        setExpandedRoleIds((prev) => new Set(prev).add(id));
      }
    })();
  }, [newTypeName, settings, persist]);

  const removeUserType = useCallback(
    (id) => {
      if (id === ADMINISTRATOR_TYPE_ID) return;
      const users = settings.users.map((u) =>
        u.userTypeId === id ? { ...u, userTypeId: ADMINISTRATOR_TYPE_ID } : u
      );
      void persist({
        ...settings,
        userTypes: settings.userTypes.filter((t) => t.id !== id),
        users,
      }).catch(() => {});
    },
    [settings, persist]
  );

  const setUserRole = useCallback(
    (userId, userTypeId) => {
      const users = settings.users.map((u) => (u.id === userId ? { ...u, userTypeId: String(userTypeId) } : u));
      void persist({ ...settings, users }).catch(() => {});
    },
    [settings, persist]
  );

  const setUserContractorName = useCallback(
    (userId, contractorName) => {
      const users = settings.users.map((u) =>
        u.id === userId ? { ...u, contractorName: String(contractorName || "") } : u
      );
      void persist({ ...settings, users }).catch(() => {});
    },
    [settings, persist]
  );

  const removeUser = useCallback(
    (userId) => {
      void persist({ ...settings, users: settings.users.filter((u) => u.id !== userId) }).catch(() => {});
    },
    [settings, persist]
  );

  const addUsers = useCallback(async () => {
    setInviteError("");
    setInviteEmailNotice("");
    setLastInviteUrls([]);
    setBatchResults([]);
    const typeId = inviteTypeId || settings.userTypes[0]?.id;
    const emails = parseBatchEmails(inviteEmailsText).filter((em) => isValidEmail(em));
    const bad = parseBatchEmails(inviteEmailsText).filter((em) => !isValidEmail(em));
    const password = addUserPassword.trim();
    if (!emails.length) {
      setInviteError(
        bad.length
          ? `No valid emails found. Check: ${bad.slice(0, 5).join(", ")}${bad.length > 5 ? "…" : ""}`
          : "Enter at least one email address."
      );
      return;
    }
    if (password && password.length < 8) {
      setInviteError("Password must be at least 8 characters, or leave blank to auto-generate one per user.");
      return;
    }
    setAddUserBusy(true);
    const results = [];
    let lastMatrix = null;
    try {
      for (const em of emails) {
        try {
          const data = await postAdminCreateUser({
            email: em,
            password: password || undefined,
            userTypeId: typeId,
          });
          if (data?.userMatrix) lastMatrix = data.userMatrix;
          results.push({
            email: em,
            ok: true,
            generatedPassword: data?.generatedPassword || null,
            detail: data?.generatedPassword
              ? `Can log in now. Temporary password: ${data.generatedPassword}`
              : "Can log in now (shared password you set).",
          });
        } catch (e) {
          results.push({
            email: em,
            ok: false,
            detail: e?.response?.data?.error || e?.message || "Could not create user.",
          });
        }
      }
      if (lastMatrix) hydrateUserMatrixFromServer(lastMatrix);
      setSettings(loadUserMatrixSettings());
      const okCount = results.filter((r) => r.ok).length;
      setBatchResults(results);
      setInviteEmailsText("");
      setAddUserPassword("");
      setInviteEmailNotice(
        `Added ${okCount} of ${results.length} user${results.length === 1 ? "" : "s"} (no invitation email sent). Share passwords securely.`
      );
      if (bad.length) {
        setInviteError(`Skipped invalid address${bad.length === 1 ? "" : "es"}: ${bad.join(", ")}`);
      }
    } finally {
      setAddUserBusy(false);
    }
  }, [inviteEmailsText, inviteTypeId, addUserPassword, settings.userTypes]);

  const sendInvites = useCallback(async () => {
    setInviteError("");
    setLastInviteUrls([]);
    setInviteEmailNotice("");
    setBatchResults([]);
    const typeId = inviteTypeId || settings.userTypes[0]?.id;
    const emails = parseBatchEmails(inviteEmailsText).filter((em) => isValidEmail(em));
    const bad = parseBatchEmails(inviteEmailsText).filter((em) => !isValidEmail(em));
    if (!emails.length) {
      setInviteError(
        bad.length
          ? `No valid emails found. Check: ${bad.slice(0, 5).join(", ")}${bad.length > 5 ? "…" : ""}`
          : "Enter at least one email address."
      );
      return;
    }

    let current = settings;
    const prepared = [];
    for (const em of emails) {
      const res = createInviteRecord(current, em, typeId);
      if (res.error) {
        prepared.push({ email: em, ok: false, detail: res.error });
        continue;
      }
      current = res.settings;
      prepared.push({
        email: em,
        ok: true,
        inviteUrl: res.inviteUrl || "",
        token: res.token || null,
        detail: "Invite saved.",
      });
    }

    const anyPrepared = prepared.some((p) => p.ok);
    if (anyPrepared) {
      const saved = await persist(current);
      if (!saved) {
        setInviteError("Could not save invites to User Matrix.");
        setBatchResults(prepared.map((p) => ({ ...p, ok: false, detail: p.ok ? "Save failed." : p.detail })));
        return;
      }
    }

    setSendInviteBusy(true);
    const urls = [];
    const results = [];
    const sentTokens = [];
    let sentCount = 0;
    let smtpMissing = false;
    try {
      for (const row of prepared) {
        if (!row.ok) {
          results.push(row);
          continue;
        }
        const url = row.inviteUrl;
        if (!url) {
          results.push({ email: row.email, ok: false, detail: "Could not build an invite link." });
          continue;
        }
        urls.push({ email: row.email, url });
        try {
          const { sent, reason } = await postUserMatrixInviteEmail(row.email, url);
          if (sent) {
            if (row.token) sentTokens.push(row.token);
            sentCount += 1;
            results.push({ email: row.email, ok: true, detail: "Invitation email sent.", inviteUrl: url });
          } else if (reason === "no_smtp") {
            smtpMissing = true;
            results.push({
              email: row.email,
              ok: true,
              detail: "Invite saved; email not sent (SMTP not configured).",
              inviteUrl: url,
            });
          } else {
            results.push({
              email: row.email,
              ok: true,
              detail: "Invite saved; email could not be sent — use Copy link.",
              inviteUrl: url,
            });
          }
        } catch (e) {
          const msg = e?.response?.data?.error || e?.message || "Request failed";
          results.push({
            email: row.email,
            ok: true,
            detail: `Invite saved; email failed: ${msg}`,
            inviteUrl: url,
          });
        }
      }
      if (sentTokens.length) await markInviteEmailsSent(sentTokens);
      setSettings(loadUserMatrixSettings());
      setLastInviteUrls(urls);
      setBatchResults(results);
      setInviteEmailsText("");
      const okCount = results.filter((r) => r.ok).length;
      if (smtpMissing) {
        setInviteEmailNotice(
          `Saved ${okCount} invite${okCount === 1 ? "" : "s"}; ${sentCount} email${sentCount === 1 ? "" : "s"} sent. SMTP is not configured on the API — use Copy links below.`
        );
      } else {
        setInviteEmailNotice(
          `Processed ${results.length}: ${sentCount} invitation email${sentCount === 1 ? "" : "s"} sent, ${okCount} invite${okCount === 1 ? "" : "s"} saved.`
        );
      }
      if (bad.length) {
        setInviteError(`Skipped invalid address${bad.length === 1 ? "" : "es"}: ${bad.join(", ")}`);
      }
    } finally {
      setSendInviteBusy(false);
    }
  }, [inviteEmailsText, inviteTypeId, settings, persist]);

  const copyInvite = useCallback(async (url) => {
    const text = String(url || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  }, []);

  const copyAllInviteLinks = useCallback(async () => {
    if (!lastInviteUrls.length) return;
    const text = lastInviteUrls.map((r) => `${r.email}\t${r.url}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  }, [lastInviteUrls]);

  useEffect(() => {
    if (!settings.userTypes.length) return;
    if (!inviteTypeId || !settings.userTypes.some((t) => t.id === inviteTypeId)) {
      setInviteTypeId(settings.userTypes[0].id);
    }
  }, [settings.userTypes, inviteTypeId]);

  const pendingByEmail = useMemo(() => {
    const m = new Map();
    for (const p of settings.pendingInvites || []) {
      if (p.email) m.set(String(p.email).toLowerCase(), p);
    }
    return m;
  }, [settings.pendingInvites]);

  return (
    <div className="email-triggers-panel user-matrix-panel">
      <h3 className="email-triggers-panel-title">User Matrix</h3>
      <p className="email-triggers-panel-lead">
        User types control which Commercial and Expansion columns members can see. Paste one or many emails
        below. <strong>Add users</strong> creates logins immediately (no invitation email). Set an optional
        shared password, or leave it blank to auto-generate one per person. <strong>Send invites</strong> emails
        signup links instead.
      </p>
      <p className="email-triggers-hint">
        <Link to="/admin/users">Open user directory (email, role, status) →</Link>
      </p>
      {saveNotice ? (
        <p className="email-triggers-muted" role="alert">
          Could not save role changes: {saveNotice}
        </p>
      ) : null}

      <section className="email-triggers-section" aria-labelledby="um-types-heading">
        <h4 id="um-types-heading" className="email-triggers-subheading">
          User types
        </h4>
        <div className="user-matrix-add-row">
          <input
            type="text"
            className="email-triggers-input"
            placeholder="New role name"
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUserType())}
          />
          <button type="button" className="email-triggers-button" onClick={addUserType}>
            Add type
          </button>
        </div>
        <ul className="user-matrix-type-list">
          {settings.userTypes.map((t) => {
            const expanded = expandedRoleIds.has(t.id);
            return (
            <li
              key={t.id}
              className={`user-matrix-type-card${expanded ? " is-expanded" : ""}`}
            >
              <div className="user-matrix-type-header">
                <button
                  type="button"
                  className="user-matrix-type-toggle"
                  onClick={() => toggleRoleExpanded(t.id)}
                  aria-expanded={expanded}
                  aria-controls={`um-role-panel-${t.id}`}
                >
                  <span className="user-matrix-type-chevron" aria-hidden="true" />
                  <span className="user-matrix-type-name">{t.name}</span>
                </button>
                {t.id !== ADMINISTRATOR_TYPE_ID ? (
                  <button
                    type="button"
                    className="email-triggers-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeUserType(t.id);
                    }}
                    aria-label={`Remove ${t.name}`}
                  >
                    Remove type
                  </button>
                ) : (
                  <span className="email-triggers-muted">Built-in</span>
                )}
              </div>
              {expanded ? (
              <div
                id={`um-role-panel-${t.id}`}
                className="user-matrix-type-columns"
              >
                <PermissionSection
                  roleType={t}
                  onChange={(permissions) => updateUserType(t.id, { permissions })}
                />
                <DepartmentTagsSection
                  roleType={t}
                  onChange={(departmentTags) => updateUserType(t.id, { departmentTags })}
                />
                {t.permissions?.canAccessCommercial !== false ? (
                  <ColumnLimitSection
                    title="Commercial table"
                    radioGroup={`um-${t.id}-commercial`}
                    ids={t.commercialColumnIds}
                    descriptors={commercialDescriptors}
                    onChange={(v) => updateUserType(t.id, { commercialColumnIds: v })}
                  />
                ) : (
                  <p className="email-triggers-muted">Commercial access disabled for this role.</p>
                )}
                {t.permissions?.canAccessExpansion !== false ? (
                  <ColumnLimitSection
                    title="Expansion table"
                    radioGroup={`um-${t.id}-expansion`}
                    ids={t.expansionColumnIds}
                    descriptors={expansionDescriptors}
                    sections={expansionDescriptorSections}
                    hint="Subsegment summary, engineering, construction, fiber, and accounting fields for CVIN expansion projects. Group row summary is not a column."
                    onChange={(v) => updateUserType(t.id, { expansionColumnIds: v })}
                  />
                ) : (
                  <p className="email-triggers-muted">Expansion access disabled for this role.</p>
                )}
              </div>
              ) : null}
            </li>
            );
          })}
        </ul>
      </section>

      <section className="email-triggers-section" aria-labelledby="um-users-heading">
        <h4 id="um-users-heading" className="email-triggers-subheading">
          Users
        </h4>
        <p className="email-triggers-hint">
          Shared role for everyone in this batch. One email still works; for several, paste one per line (or
          separate with commas).
        </p>
        <div className="user-matrix-invite-stack">
          <textarea
            className="email-triggers-input user-matrix-invite-emails"
            rows={4}
            placeholder={"colleague@cvin.com\nanother@vastnetworks.com"}
            value={inviteEmailsText}
            onChange={(e) => setInviteEmailsText(e.target.value)}
            aria-label="Email addresses to add or invite"
          />
          <div className="user-matrix-invite-row">
            <select
              className="user-matrix-select"
              value={inviteTypeId || ""}
              onChange={(e) => setInviteTypeId(e.target.value)}
              aria-label="Role for new users"
            >
              {settings.userTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            {inviteEmailCount > 0 ? (
              <span className="email-triggers-muted">
                {inviteEmailCount} valid email{inviteEmailCount === 1 ? "" : "s"}
                {invalidInviteEmails.length
                  ? ` · ${invalidInviteEmails.length} invalid skipped`
                  : ""}
              </span>
            ) : null}
          </div>
        </div>
        <div className="user-matrix-invite-row" style={{ marginTop: "0.5rem" }}>
          <input
            type="password"
            className="email-triggers-input"
            placeholder="Password for Add users (optional — auto-generated if blank)"
            value={addUserPassword}
            onChange={(e) => setAddUserPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!addUserBusy && inviteEmailCount > 0) void addUsers();
              }
            }}
            autoComplete="new-password"
            aria-label="Password for Add users"
          />
          <button
            type="button"
            className="email-triggers-button email-triggers-button-primary"
            onClick={addUsers}
            disabled={inviteEmailCount === 0 || addUserBusy || sendInviteBusy}
          >
            {addUserBusy
              ? "Adding…"
              : inviteEmailCount > 1
                ? `Add ${inviteEmailCount} users`
                : "Add user"}
          </button>
        </div>
        <div className="user-matrix-invite-row" style={{ marginTop: "0.5rem" }}>
          <button
            type="button"
            className="email-triggers-button"
            onClick={sendInvites}
            disabled={inviteEmailCount === 0 || sendInviteBusy || addUserBusy}
          >
            {sendInviteBusy
              ? "Sending…"
              : inviteEmailCount > 1
                ? `Send ${inviteEmailCount} invites`
                : "Send invite"}
          </button>
        </div>
        {inviteError ? (
          <p className="project-detail-error" role="alert">
            {inviteError}
          </p>
        ) : null}
        {inviteEmailNotice ? (
          <p className="email-triggers-muted" role="status">
            {inviteEmailNotice}
          </p>
        ) : null}
        {batchResults.length > 0 ? (
          <div className="user-matrix-batch-results" role="status">
            <ul className="user-matrix-batch-results-list">
              {batchResults.map((r) => (
                <li key={r.email} className={r.ok ? "is-ok" : "is-fail"}>
                  <strong>{r.email}</strong>
                  <span>{r.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {lastInviteUrls.length > 0 ? (
          <div className="user-matrix-invite-result">
            {lastInviteUrls.length === 1 ? (
              <>
                <span className="user-matrix-invite-url" title={lastInviteUrls[0].url}>
                  {lastInviteUrls[0].url}
                </span>
                <button
                  type="button"
                  className="email-triggers-button"
                  onClick={() => copyInvite(lastInviteUrls[0].url)}
                >
                  Copy link
                </button>
              </>
            ) : (
              <>
                <span className="email-triggers-muted">
                  {lastInviteUrls.length} invite links ready
                </span>
                <button type="button" className="email-triggers-button" onClick={copyAllInviteLinks}>
                  Copy all links
                </button>
              </>
            )}
          </div>
        ) : null}

        <p className="email-triggers-hint">
          For the <strong>Contractor</strong> role, set <strong>Contractor name</strong> to the exact company/person
          name used on construction or fiber contractor fields. They only see matching projects.
        </p>
        <div className="email-triggers-table-wrap">
          <table className="email-triggers-table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Contractor name</th>
                <th scope="col">Status</th>
                <th scope="col">Invite email</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {settings.users.map((u) => {
                const pend = pendingByEmail.get(String(u.email).toLowerCase());
                const emailed =
                  u.status === "pending" && pend?.inviteEmailSentAt
                    ? "Sent"
                    : u.status === "pending"
                      ? "Not sent yet"
                      : "—";
                const roleName = String(
                  settings.userTypes.find((t) => t.id === u.userTypeId)?.name || ""
                )
                  .trim()
                  .toLowerCase();
                const isContractorUser =
                  u.userTypeId === CONTRACTOR_TYPE_ID || roleName === "contractor";
                return (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>
                      <select
                        className="user-matrix-select"
                        value={u.userTypeId}
                        onChange={(e) => setUserRole(u.id, e.target.value)}
                        aria-label={`Role for ${u.email}`}
                      >
                        {settings.userTypes.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {isContractorUser ? (
                        <input
                          type="text"
                          className="email-triggers-input"
                          value={u.contractorName || ""}
                          onChange={(e) => setUserContractorName(u.id, e.target.value)}
                          placeholder="e.g. ABC Construction"
                          aria-label={`Contractor name for ${u.email}`}
                        />
                      ) : (
                        <span className="email-triggers-muted">—</span>
                      )}
                    </td>
                    <td>{u.status === "pending" ? "Pending invite" : "Active"}</td>
                    <td className="email-triggers-muted">{emailed}</td>
                    <td>
                      <button type="button" className="email-triggers-remove" onClick={() => removeUser(u.id)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {settings.users.length === 0 ? <p className="email-triggers-muted">No users yet.</p> : null}
        </div>
      </section>
    </div>
  );
}
